/**
 * Tile-level heuristics shared by the bot and the coach.
 * Pure functions of tiles and counts: no engine state is ever modified.
 */

import { TILE_INFO, countKinds, kindAtIndex, kindIndex } from "@kikik/mahjong-core";
import type { Tile, TileKind } from "@kikik/mahjong-core";

export type KeepReason =
  | "triplet"
  | "pair"
  | "run-part"
  | "run-gap"
  | "isolated"
  | "lone-honor"
  | "lone-terminal";

export interface KeepScore {
  score: number;
  reason: KeepReason;
  /** Tiles in hand that make this tile worth keeping. */
  connections: TileKind[];
}

export const KEEP_WEIGHTS = {
  triplet: 120,
  pair: 70,
  neighbour: 26,
  gapNeighbour: 14,
  terminalPenalty: -6,
  isolatedHonor: -12,
} as const;

function countOf(counts: readonly number[], index: number): number {
  return counts[index] ?? 0;
}

/**
 * How much the hand wants to keep this tile.
 * Complete sets rank highest, then pairs, then tiles with sequence potential;
 * lone honors and lone terminals rank lowest and are discarded first.
 */
export function scoreTileForKeeping(kind: TileKind, counts: readonly number[]): KeepScore {
  const info = TILE_INFO[kind];
  const index = kindIndex(kind);
  const copies = countOf(counts, index);
  const connections: TileKind[] = [];

  let score = 0;
  let reason: KeepReason;

  if (copies >= 3) {
    score += KEEP_WEIGHTS.triplet;
    reason = "triplet";
  } else if (copies === 2) {
    score += KEEP_WEIGHTS.pair;
    reason = "pair";
  } else {
    reason = "isolated";
  }

  if (info.isHonor) {
    if (copies === 1) {
      score += KEEP_WEIGHTS.isolatedHonor;
      reason = "lone-honor";
    }
    return { score, reason, connections };
  }

  let adjacent = 0;
  for (const delta of [-2, -1, 1, 2]) {
    const neighbourRank = info.rank + delta;
    if (neighbourRank < 1 || neighbourRank > 9) continue;
    if (countOf(counts, index + delta) === 0) continue;
    connections.push(kindAtIndex(index + delta));
    if (Math.abs(delta) === 1) {
      adjacent += 1;
      score += KEEP_WEIGHTS.neighbour;
    } else {
      score += KEEP_WEIGHTS.gapNeighbour;
    }
  }

  if (copies < 2 && connections.length > 0) reason = adjacent > 0 ? "run-part" : "run-gap";
  if (info.isTerminal) {
    score += KEEP_WEIGHTS.terminalPenalty;
    if (reason === "isolated") reason = "lone-terminal";
  }

  return { score, reason, connections };
}

export interface DiscardCandidate {
  tile: Tile;
  /** Higher means more useful to keep. */
  keepScore: number;
  reason: KeepReason;
  connections: TileKind[];
}

/** Concealed tiles ranked worst-to-keep first. */
export function rankTilesForDiscard(concealed: readonly Tile[]): DiscardCandidate[] {
  const counts = countKinds(concealed.map((tile) => tile.kind));
  return concealed
    .map((tile) => {
      const scored = scoreTileForKeeping(tile.kind, counts);
      return {
        tile,
        keepScore: scored.score,
        reason: scored.reason,
        connections: scored.connections,
      };
    })
    .sort((a, b) => {
      if (a.keepScore !== b.keepScore) return a.keepScore - b.keepScore;
      const byKind = kindIndex(a.tile.kind) - kindIndex(b.tile.kind);
      return byKind !== 0 ? byKind : a.tile.id - b.tile.id;
    });
}
